import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Wrench,
  Television,
  Broadcast,
  Desktop,
  CellTower,
  VideoCamera,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  CaretRight,
  Sparkle,
} from "@phosphor-icons/react";

import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppFab } from "../components/WhatsAppFab";
import { CctvCompare } from "../components/CctvCompare";
import { VsatBandCompare } from "../components/VsatBandCompare";
import { useLang } from "../context/LanguageContext";
import {
  servicesData,
  servicePageStrings,
  SERVICE_SLUGS,
} from "../lib/servicesData";
import { useSeo } from "../lib/useSeo";

const ICONS = { Wrench, Television, Broadcast, Desktop, CellTower, VideoCamera };

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const data = servicesData[slug];
  const labels = servicePageStrings[lang] || servicePageStrings.pt;

  const serviceContent = data ? t.services[data.key] : null;
  const Icon = data ? ICONS[data.icon] || Wrench : Wrench;
  const seo = data
    ? data.seo[lang] || data.seo.pt
    : { title: "TVCabSat", description: "" };
  useSeo(seo);

  const otherSlugs = SERVICE_SLUGS.filter((s) => s !== slug);

  if (!data || !serviceContent) {
    return (
      <div className="min-h-screen bg-[#a8c8e2] text-slate-900">
        <Header />
        <main className="mx-auto max-w-3xl px-6 pt-40 pb-24 text-center">
          <h1 className="font-heading text-3xl font-bold">404</h1>
          <p className="mt-3 text-slate-600">Service not found.</p>
          <Link
            to="/"
            className="mt-6 inline-block rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white"
          >
            {labels.back}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      data-testid={`service-page-${slug}`}
      className="relative min-h-screen bg-[#a8c8e2] text-slate-900"
    >
      <Header />

      <section className="relative isolate overflow-hidden border-b border-slate-200 bg-gradient-to-b from-[#d6e6f2] via-[#b8d6ef] to-[#a8c8e2]">
        <div className="absolute inset-0 -z-10 opacity-20">
          <img
            src={data.hero}
            alt=""
            aria-hidden
            className="h-full w-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#d8e8f5] via-[#d8e8f5]/80 to-white/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#a8c8e2] via-transparent to-white/60" />
        </div>
        <div className="pointer-events-none absolute -top-32 -right-32 -z-10 h-[400px] w-[400px] rounded-full bg-blue-400/15 blur-3xl" />

        <div className="mx-auto max-w-[1400px] px-6 pb-20 pt-12 md:px-12 md:pt-16 lg:px-16 lg:pb-28 lg:pt-20">
          <nav
            data-testid="service-breadcrumb"
            aria-label="Breadcrumb"
            className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500"
          >
            <Link
              to="/"
              className="transition-colors hover:text-slate-900"
              data-testid="breadcrumb-home"
            >
              {labels.breadcrumb_home}
            </Link>
            <CaretRight size={10} className="text-slate-400" />
            <Link
              to="/#services"
              className="transition-colors hover:text-slate-900"
              data-testid="breadcrumb-services"
            >
              {labels.breadcrumb_services}
            </Link>
            <CaretRight size={10} className="text-slate-400" />
            <span className="text-blue-600">{serviceContent.title}</span>
          </nav>

          <div className="mt-10 flex items-start gap-5">
            <div className="icon-tile mt-2 shrink-0">
              <Icon size={26} weight="duotone" />
            </div>
            <h1
              data-testid="service-title"
              className="font-heading text-3xl font-black leading-[0.95] tracking-tighter text-slate-900 sm:text-4xl md:text-5xl lg:text-6xl"
            >
              {serviceContent.title}
            </h1>
          </div>

          <p
            data-testid="service-description"
            className="mt-8 max-w-2xl text-base leading-relaxed text-slate-700 md:text-lg"
          >
            {data.longDesc[lang] || data.longDesc.pt}
          </p>

          <div className="mt-10 flex gap-3">
            <button
              type="button"
              onClick={() => navigate("/#contact")}
              data-testid="service-cta-contact"
              className="group inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:bg-blue-500 hover:shadow-blue-600/40 active:scale-95"
            >
              {labels.cta_btn}
              <ArrowRight
                size={16}
                weight="bold"
                className="transition-transform group-hover:translate-x-1"
              />
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              data-testid="service-back"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3.5 text-sm font-semibold text-slate-700 shadow-sm hover:border-slate-400 hover:bg-slate-50"
            >
              <ArrowLeft size={14} weight="bold" />
              {labels.back}
            </button>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-5">
              <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-blue-600">
                {labels.highlights_title}
              </div>
              <h2 className="font-heading mt-4 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
                {serviceContent.desc}
              </h2>
              <ul className="mt-8 space-y-3">
                {(data.highlights[lang] || data.highlights.pt).map((h) => (
                  <li
                    key={h}
                    className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-800 shadow-sm transition-colors hover:border-blue-300"
                  >
                    <Sparkle
                      size={16}
                      weight="fill"
                      className="mt-0.5 shrink-0 text-blue-600"
                    />
                    {h}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-7">
              <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-blue-600">
                {labels.scope_title}
              </div>
              <h2 className="font-heading mt-4 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
                {serviceContent.title}
              </h2>
              <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {serviceContent.items.map((item) => (
                  <div
                    key={item}
                    data-testid={`scope-item-${item}`}
                    className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-colors hover:border-blue-300"
                  >
                    <CheckCircle
                      size={18}
                      weight="fill"
                      className="mt-0.5 shrink-0 text-blue-600"
                    />
                    <span className="text-sm text-slate-800">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-[#dfecf6] py-24 lg:py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="flex items-end justify-between gap-6">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-blue-600">
                {labels.gallery_title}
              </div>
              <h2 className="font-heading mt-4 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
                {serviceContent.title}
              </h2>
            </div>
            <div className="hidden max-w-xs text-xs text-slate-500 md:block">
              {labels.gallery_note}
            </div>
          </div>

          <div
            data-testid="service-gallery"
            className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
          >
            {data.gallery.map((src, i) => (
              <div
                key={src}
                className={`hover-lift group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm ${
                  i === 0 ? "sm:col-span-2 sm:row-span-2" : ""
                }`}
              >
                <div className="relative aspect-[4/3]">
                  <img
                    src={src}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
                  <div className="pointer-events-none absolute left-4 top-4 rounded-full border border-white/30 bg-black/45 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-white backdrop-blur">
                    {String(i + 1).padStart(2, "0")} · {serviceContent.title}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 text-xs text-slate-500 md:hidden">
            {labels.gallery_note}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-[#d8e8f5] via-[#d8e8f5] to-blue-50 p-10 shadow-xl md:p-16">
            <div className="pointer-events-none absolute -right-32 -top-32 h-80 w-80 rounded-full bg-blue-400/25 blur-3xl" />
            <div className="relative flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
              <div>
                <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
                  {labels.cta_title}
                </h2>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-600 md:text-base">
                  {labels.cta_desc}
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/#contact")}
                data-testid="service-cta-bottom"
                className="group inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-4 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:bg-blue-500 hover:shadow-blue-600/40 active:scale-95"
              >
                {labels.cta_btn}
                <ArrowRight
                  size={16}
                  weight="bold"
                  className="transition-transform group-hover:translate-x-1"
                />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CCTV-only: camera-family decision helper rendered just before the
          bottom CTA to guide visitors that are still picking a tech. */}
      {slug === "seguranca-cctv" && <CctvCompare />}

      {/* Communications: VSAT band decision helper (C / Ku / Ka) — critical
          decision for remote sites in Angola's tropical climate. */}
      {slug === "comunicacoes" && <VsatBandCompare />}

      <section className="border-t border-slate-200 pb-28 pt-16">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-blue-600">
            {labels.other_services}
          </div>
          <div
            data-testid="other-services"
            className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3"
          >
            {otherSlugs.map((s) => {
              const other = servicesData[s];
              const OtherIcon = ICONS[other.icon] || Wrench;
              const name = t.services[other.key].title;
              return (
                <Link
                  key={s}
                  to={`/servicos/${s}`}
                  data-testid={`other-service-${s}`}
                  className="group flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-colors hover:border-blue-300"
                >
                  <div className="flex items-center gap-4">
                    <div className="icon-tile">
                      <OtherIcon size={20} weight="duotone" />
                    </div>
                    <div className="font-heading text-lg font-semibold text-slate-900">
                      {name}
                    </div>
                  </div>
                  <ArrowRight
                    size={18}
                    weight="bold"
                    className="text-slate-400 transition-all group-hover:translate-x-1 group-hover:text-blue-600"
                  />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFab />
    </div>
  );
}
